import { appStorage } from './betaStorage'
import { useEffect, useState } from 'react'

export const adultHomeTiles = {
  alex: [
    { id: 'greeting', label: 'Good morning', to: '/today' },
    { id: 'dinner', label: 'What’s for dinner?', to: '/care-menu' },
    { id: 'support', label: 'Who’s supporting me', to: '/team' },
    { id: 'upcoming', label: 'Coming up', to: '/calendar' },
    { id: 'wellbeing', label: 'My wellbeing', to: '/profile' },
    { id: 'tasks', label: 'Tasks & routines', to: '/tasks' },
    { id: 'shopping', label: 'Shopping list', to: '/shopping' },
    { id: 'medication', label: 'Medication', to: '/medication' },
    { id: 'goals', label: 'Goals', to: '/goals' },
  ],
  mark: [
    { id: 'greeting', label: 'Alex today', to: '/home' },
    { id: 'dinner', label: 'What’s for dinner?', to: '/care-menu' },
    { id: 'support', label: 'Who’s with Alex today', to: '/team' },
    { id: 'updates', label: 'Team updates', to: '/updates' },
    { id: 'funding', label: 'Funding', to: '/funding' },
    { id: 'upcoming', label: 'Coming up', to: '/calendar' },
    { id: 'tasks', label: 'Tasks & shift activity', to: '/tasks' },
    { id: 'latest', label: 'Latest team updates', to: '/updates' },
    { id: 'team', label: 'Alex’s team', to: '/team' },
    { id: 'messages', label: 'Messages', to: '/chats' },
  ],
} as const

export type AdultHomeProfile = keyof typeof adultHomeTiles
export type AdultHomePrefs = { order: string[]; visible: string[] }
const key = (userId: string) => `marnixia.adultHome.v1.${userId}`
const idsFor = (profile: AdultHomeProfile) => adultHomeTiles[profile].map(tile => tile.id as string)

function readArray(storageKey: string): string[] {
  try { const value: unknown = JSON.parse(appStorage.getItem(storageKey) || 'null'); return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [] }
  catch { return [] }
}

export function loadAdultHomePrefs(userId: string, profile: AdultHomeProfile): AdultHomePrefs {
  const ids = idsFor(profile)
  try {
    const saved: unknown = JSON.parse(appStorage.getItem(key(userId)) || 'null')
    if (saved && typeof saved === 'object' && 'order' in saved && 'visible' in saved && Array.isArray(saved.order) && Array.isArray(saved.visible)) {
      return {
        order: [...new Set([...saved.order.filter((id): id is string => typeof id === 'string' && ids.includes(id)), ...ids])],
        visible: [...new Set(saved.visible.filter((id): id is string => typeof id === 'string' && ids.includes(id)))],
      }
    }
  } catch { /* use defaults or legacy Alex preferences */ }

  if (profile === 'alex') {
    const oldQuickOrder = readArray('schub.alexHomeTiles').filter(id => ids.includes(id))
    const oldFlowOrder = readArray(`marnixia.todayFlow.v1.${userId}.adult-participant-today`).filter(id => id === 'dinner' || id === 'support')
    const hidden = new Set(readArray('schub.alexHomeHiddenTiles'))
    return {
      order: [...new Set(['greeting', ...oldFlowOrder, 'dinner', 'support', 'upcoming', ...oldQuickOrder, ...ids])],
      visible: ids.filter(id => !hidden.has(id)),
    }
  }
  return { order: ids, visible: ids }
}

export function saveAdultHomePrefs(userId: string, prefs: AdultHomePrefs) {
  appStorage.setItem(key(userId), JSON.stringify(prefs))
}

export function useAdultHomePrefs(userId: string, profile: AdultHomeProfile) {
  const [prefs, setPrefs] = useState(() => loadAdultHomePrefs(userId, profile))
  useEffect(() => { setPrefs(loadAdultHomePrefs(userId, profile)) }, [userId, profile])
  const change = (next: AdultHomePrefs) => { setPrefs(next); saveAdultHomePrefs(userId, next) }
  const toggle = (id: string) => change({ ...prefs, visible: prefs.visible.includes(id) ? prefs.visible.filter(value => value !== id) : [...prefs.visible, id] })
  const move = (id: string, direction: -1 | 1) => {
    const order = [...prefs.order], index = order.indexOf(id), target = index + direction
    if (index < 0 || target < 0 || target >= order.length) return
    ;[order[index], order[target]] = [order[target], order[index]]
    change({ ...prefs, order })
  }
  const restore = () => { const ids = idsFor(profile); change({ order: ids, visible: ids }) }
  return { prefs, toggle, move, restore }
}
